import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import {
  Category,
  ConnectedProperty,
  GA4PropertiesApiService,
  LookupPropertiesRequest,
  LookupPropertiesResponse,
} from '@vendasta/google-analytics';
import { differenceInMinutes } from 'date-fns';
import { parseDate } from '@vendasta/galaxy/utility/date-utils';
import { BehaviorSubject, Observable, ReplaySubject, from, of } from 'rxjs';
import { catchError, filter, map, switchMap, take, tap } from 'rxjs/operators';
import { VDC_APP_ID } from '../../../globals';
import { getCookie } from '../../core/cookie';
import { SiteService } from '../../core/site';
import { GaEnabledResponse } from '../../core/site/site';
import { AnalyticsJSON } from './analytics.interfaces';

// gapi is the analytics script available on the DOM, loaded through the Google
// Analytics JS script
declare const gapi: { analytics: any };
const SITE_URL_V2 = '/api/v2/site/';

@Injectable()
export class AnalyticsService {
  viewId: string;

  private connectGALink$$: ReplaySubject<string> = new ReplaySubject<string>(1);
  private connectGALinkLoading$$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);
  private connectGALinkError$$: ReplaySubject<any> = new ReplaySubject<any>(1);
  private gaViewNotFound$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);
  private ga4ViewNotFound$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);
  private gaViewLoading$$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);
  private gaViewCollectionRunning$$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);
  private gaViewError$$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);
  private ga4ViewError$$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);
  private ga4View$$: BehaviorSubject<ConnectedProperty> = new BehaviorSubject<ConnectedProperty>(null);

  ga4View$ = this.ga4View$$.asObservable();
  connectGALink$ = this.connectGALink$$.asObservable();
  connectGALinkLoading$ = this.connectGALinkLoading$$.asObservable();
  connectGALinkError$ = this.connectGALinkLoading$$.asObservable();
  gaViewNotFound$: Observable<boolean> = this.gaViewNotFound$$.asObservable();
  ga4ViewNotFound$: Observable<boolean> = this.ga4ViewNotFound$$.asObservable();
  gaViewLoading$: Observable<boolean> = this.gaViewLoading$$.asObservable();
  gaViewError$: Observable<boolean> = this.gaViewError$$.asObservable();
  ga4ViewError$: Observable<boolean> = this.ga4ViewError$$.asObservable();
  gaViewCollectionRunning$: Observable<boolean> = this.gaViewCollectionRunning$$.asObservable();
  private headers = new HttpHeaders().set('Content-Type', 'application/json');
  private isGaEnabledSource = new BehaviorSubject(false);
  isGaEnabled = this.isGaEnabledSource.asObservable();

  constructor(
    private http: HttpClient,
    private router: Router,
    private siteService: SiteService,
    private ga4ViewService: GA4PropertiesApiService,
  ) {
    this.gaViewLoading$$.next(true);
    this.gaViewError$$.next(false);
    this.ga4ViewError$$.next(false);
    this.siteService.site$
      .pipe(
        tap(() => this.connectGALinkLoading$$.next(true)),
        switchMap((site) => this.getConnectGALink(site.accountId)),
        tap((connectGALink) => {
          this.connectGALink$$.next(connectGALink);
          this.connectGALinkLoading$$.next(false);
          this.connectGALinkError$$.next(false);
        }),
        catchError(() => {
          this.connectGALink$$.next('');
          this.connectGALinkLoading$$.next(false);
          this.connectGALinkError$$.next(true);
          return of('');
        }),
      )
      .subscribe();
    this.siteService.site$
      .pipe(
        filter((s) => !!s.accountId && !!s.partnerId),
        take(1),
        switchMap((s) =>
          this.ga4ViewService.lookupProperties(
            new LookupPropertiesRequest({
              partnerId: s.partnerId,
              businessId: s.accountId,
              category: Category.CATEGORY_WEBSITE,
            }),
          ),
        ),
        map((resp: LookupPropertiesResponse) => resp.properties),
      )
      .subscribe((properties: ConnectedProperty[]) => {
        this.gaViewLoading$$.next(false);
        if (properties && properties.length > 0) {
          this.gaViewCollectionRunning$$.next(this.isCollectionRunning(properties[0].created));
          this.ga4View$$.next(properties[0]);
          this.ga4ViewNotFound$$.next(false);
        } else {
          this.ga4ViewNotFound$$.next(true);
        }
        return;
      });
    catchError(() => {
      this.gaViewLoading$$.next(false);
      this.gaViewError$$.next(true);
      this.ga4ViewError$$.next(true);
      this.gaViewNotFound$$.next(true);
      this.ga4ViewNotFound$$.next(true);
      return of(null);
    });
  }

  /**
   * Authenticates with the legacy Google Analytics connection by getting authentication
   * data from the backend
   * @param siteId
   */
  authenticate(siteId: string): Observable<AnalyticsJSON> {
    const url = `/api/v1/analytics/${siteId}/authenticate`;
    return this.http.get(url).pipe(map((res: { data: any }) => res.data));
  }

  /**
   * Authorizes the current session with Google Analytics by placing the view
   * ID and the access token on the DOM gapi script in order to query Google
   * Analytics in future requests
   * @param siteId - ID of the site to authenticate, then authorize
   * @param successCallback - function that takes in a view ID to query Google
   * Analytics with
   */
  authorize(siteId: string, successCallback: (viewId: string) => void): void {
    this.authenticate(siteId).subscribe((data) => {
      this.viewId = data.view_id;
      const accessToken = data.access_token;
      gapi.analytics.ready(function (): void {
        gapi.analytics.auth.authorize({
          serverAuth: {
            access_token: accessToken,
          },
        });
        gapi.analytics.auth.on('success', successCallback(data.view_id));
      });
    });
  }

  /**
   * Constructs the query structure that is sent to Google Analytics for getting data about a specific GA view
   * @param params - return of getQueryOptions, which structured the query for the Google Analytics API
   */
  buildQuery(params: any): Observable<any> {
    const promise = new Promise((resolve, reject) => {
      const data = new gapi.analytics.report.Data({ query: params });
      data.once('success', resolve).once('error', reject).execute();
    });
    return from(promise);
  }

  /**
   * Returns a Google Analytics connection link that takes users to the Social Connections section of the Business App
   * in order to establish their connection with Google Analytics
   * @param accountId - the account group ID of the account associated with the site for which the connection should
   * be established
   */
  getConnectGALink(accountId: string): Observable<string> {
    const prodNavURL = getCookie('product_navbar_data_url');
    const navbarUrl = `${prodNavURL}?accountId=${accountId}&appId=${VDC_APP_ID}`;
    return this.http
      .get<any>(navbarUrl)
      .pipe(
        map((response) =>
          this.appendForwardingParamsToURL(response.data.urls.social_connections_url + '/GoogleAnalytics'),
        ),
      );
  }

  /**
   * Constructs the final redirect URL to the Social Connections page of Business App
   * @param socialConnectionsUrl
   */
  appendForwardingParamsToURL(socialConnectionsUrl: string): string {
    const returnUrl = encodeURIComponent(window.location.href);
    return `${socialConnectionsUrl}?returnUrl=${returnUrl}&forwardTo=google-analytics`;
  }

  /**
   * Tells whether the Google Analytics data collection is still running. This
   * refers to the connection that users establish through Social Connections.
   * The user will get a message if the collection is still running.
   * @param created - date that the connection was established on
   */
  isCollectionRunning(created: Date): boolean {
    const parsed = parseDate(created);
    if (!parsed) return true;
    return differenceInMinutes(new Date(), parsed) < 15;
  }

  getGaIsEnabledState(siteId: string) {
    const url = `${SITE_URL_V2}${siteId}/google-analytics-state/`;
    const options = { headers: this.headers };
    return this.http.get(url, options).pipe(
      map((res: GaEnabledResponse) => {
        this.setIsEnabledState(res.data.enabled);
      }),
    );
  }

  setIsEnabledState(enabled) {
    this.isGaEnabledSource.next(enabled);
  }
}
